import React, { useState, useEffect } from 'react';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Pencil, Key, Check, AlertTriangle, Lock, Info, Users, Calculator, Save, X, UserPlus, Copy, CheckCircle, Trash2 } from 'lucide-react';

const MASTER_ADMIN_EMAIL = 'gmpdeveloppement@gmail.com';

const Coproprietaires = ({ user }) => {
  const [coproprietaires, setCoproprietaires] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editData, setEditData] = useState({});
  const [editMode, setEditMode] = useState(false); // Mode édition des tantièmes
  const [tantièmesEdit, setTantièmesEdit] = useState({}); // {userId: tantièmes}
  const [newCopro, setNewCopro] = useState({
    prenom: '',
    nom: '',
    email: '',
    telephone: '',
    lot: '',
    role: 'copropriétaire',
    tantiemes: 0
  });
  const [addResult, setAddResult] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {id, name} du copro à supprimer
  const [isDeleting, setIsDeleting] = useState(false);

  const isMasterAdmin = user.email?.toLowerCase() === MASTER_ADMIN_EMAIL.toLowerCase();
  const isPresident = user.role === 'président' || isMasterAdmin;
  const isAdmin = user.role === 'président' || user.role === 'trésorier';

  useEffect(() => {
    fetchCoproprietaires();
  }, []);

  const fetchCoproprietaires = async () => {
    try {
      const response = await apiClient.get('/coproprietaires');
      setCoproprietaires(response.data);
      // Initialiser les tantièmes pour l'édition
      const tantiemes = {};
      response.data.forEach(c => {
        tantiemes[c.id] = c.tantiemes || 0;
      });
      setTantièmesEdit(tantiemes);
    } catch (error) {
      toast.error('Erreur lors du chargement des copropriétaires');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (copro) => {
    setSelectedUser(copro);
    setEditData({
      prenom: copro.prenom || '',
      nom: copro.nom || '',
      telephone: copro.telephone || '',
      email: copro.email || '',
      lot: copro.lot || '',
      role: copro.role || 'copropriétaire',
    });
    setEditModal(true);
  };

  const handleDeleteCoproprietaire = async () => {
    if (!deleteConfirm) return;
    
    setIsDeleting(true);
    try {
      const response = await apiClient.delete(`/coproprietaires/${deleteConfirm.id}`);
      toast.success(response.data.message || 'Copropriétaire supprimé');
      
      if (response.data.needs_rebalancing) {
        toast.info(`Total des tantièmes: ${response.data.new_total_tantiemes}. Pensez à rééquilibrer via "Tantièmes".`, {
          duration: 5000
        });
      }
      
      setDeleteConfirm(null);
      fetchCoproprietaires();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      await apiClient.put(`/coproprietaires/${selectedUser.id}`, editData);
      toast.success('Informations mises à jour');
      setEditModal(false);
      fetchCoproprietaires();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la mise à jour');
      if (message) toast.error(message);
    }
  };

  // Calculer le total des tantièmes
  const totalTantiemes = Object.values(tantièmesEdit).reduce((sum, t) => sum + (parseFloat(t) || 0), 0);
  const tantièmesValides = Math.abs(totalTantiemes - 1000) < 0.01;

  // Mise à jour des tantièmes en mode édition
  const handleTantièmeChange = (userId, value) => {
    setTantièmesEdit(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  // Sauvegarder tous les tantièmes
  const saveTantiemes = async () => {
    if (!tantièmesValides) {
      toast.error(`Le total des tantièmes doit être exactement 1000 (actuellement: ${totalTantiemes.toFixed(2)})`);
      return;
    }

    try {
      // Envoyer la mise à jour pour chaque copropriétaire
      const updates = Object.entries(tantièmesEdit).map(([userId, tantiemes]) => 
        apiClient.put(`/coproprietaires/${userId}/tantiemes`, { tantiemes: parseFloat(tantiemes) })
      );
      
      await Promise.all(updates);
      toast.success('Tantièmes mis à jour avec succès');
      setEditMode(false);
      fetchCoproprietaires();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de la mise à jour des tantièmes');
      if (message) toast.error(message);
    }
  };

  // Calculer le nouveau total si on ajoute ce copropriétaire
  // Validation pour l'ajout : on permet d'ajouter à 0 tantièmes, le rééquilibrage se fait après
  const newTotalWithAdd = totalTantiemes + (parseFloat(newCopro.tantiemes) || 0);
  // Valide si : ajout à 0 (rééquilibrage après) OU total = 1000
  const newTotalValid = (parseFloat(newCopro.tantiemes) || 0) === 0 || Math.abs(newTotalWithAdd - 1000) < 0.01;

  const openAddModal = () => {
    setNewCopro({
      prenom: '',
      nom: '',
      email: '',
      telephone: '',
      lot: '',
      role: 'copropriétaire',
      tantiemes: 0
    });
    setAddResult(null);
    setAddModal(true);
  };

  const handleAddCoproprietaire = async () => {
    if (!newCopro.prenom || !newCopro.nom) {
      toast.error('Le prénom et le nom sont obligatoires');
      return;
    }

    if (parseFloat(newCopro.tantiemes) !== 0 && !newTotalValid) {
      toast.error(`Le total des tantièmes doit être exactement 1000. Avec cet ajout: ${newTotalWithAdd.toFixed(2)}`);
      return;
    }

    try {
      const response = await apiClient.post('/coproprietaires', newCopro);
      
      if (response.data.provisional_password) {
        setAddResult({
          success: true,
          provisionalPassword: response.data.provisional_password,
          userName: `${newCopro.prenom} ${newCopro.nom}`,
          email: newCopro.email
        });
        toast.success('Copropriétaire ajouté avec succès');
      } else {
        toast.success(response.data.message || 'Copropriétaire ajouté');
        setAddModal(false);
      }
      
      fetchCoproprietaires();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur lors de l\'ajout');
      if (message) toast.error(message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papier');
  };

  const handleMarkPasswordTransmitted = async (coproId, coproName) => {
    const confirmed = window.confirm(
      `Confirmez-vous avoir transmis le mot de passe provisoire à ${coproName} ?\n\nUne fois confirmé, le mot de passe ne sera plus visible.`
    );
    
    if (!confirmed) return;

    try {
      await apiClient.put(`/coproprietaires/${coproId}/password-transmitted`);
      toast.success('Mot de passe marqué comme transmis');
      fetchCoproprietaires();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur');
      if (message) toast.error(message);
    }
  };

  // Vérifier si l'utilisateur peut modifier un copropriétaire
  const canEdit = (copro) => {
    // Master admin peut tout modifier
    if (isMasterAdmin) return true;
    // Le président peut modifier tous les copropriétaires
    if (isPresident) return true;
    // L'utilisateur peut modifier ses propres infos
    if (copro.id === user.id) return true;
    return false;
  };

  // Compter les mots de passe à transmettre
  const passwordsToTransmit = coproprietaires.filter(c => c.provisional_password && c.must_change_password).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="coproprietaires-page">
      <div className="mb-6 md:mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Copropriétaires</h1>
        <p className="text-slate-500 text-base md:text-lg">Liste des membres de la copropriété</p>
      </div>

      {/* Alerte mots de passe à transmettre */}
      {isAdmin && passwordsToTransmit > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Key className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-900">
                {passwordsToTransmit} mot{passwordsToTransmit > 1 ? 's' : ''} de passe à transmettre
              </h3>
              <p className="text-sm text-amber-700 mt-1">
                Transmettez les mots de passe provisoires aux copropriétaires concernés, 
                puis cochez la case pour confirmer la transmission.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Info sur les restrictions + Bouton édition tantièmes */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-slate-600">
              {isPresident ? (
                <p>
                  <strong>En tant que président</strong>, vous pouvez modifier les informations de tous les copropriétaires 
                  ainsi que les tantièmes (le total doit toujours être égal à 1000).
                </p>
              ) : (
                <p>
                  Chaque copropriétaire peut modifier ses propres informations personnelles (prénom, nom, téléphone).
                  Seul le président peut modifier les tantièmes.
                </p>
              )}
            </div>
          </div>
        </div>
        
        {isPresident && (
          <div className="flex items-center gap-2">
            {editMode ? (
              <>
                <div className={`px-4 py-2 rounded-lg font-medium ${tantièmesValides ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                  <Calculator className="w-4 h-4 inline mr-2" />
                  Total: {totalTantiemes.toFixed(2)} / 1000
                </div>
                <Button onClick={saveTantiemes} disabled={!tantièmesValides} className="bg-emerald-500 hover:bg-emerald-600">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
                <Button variant="outline" onClick={() => { setEditMode(false); fetchCoproprietaires(); }}>
                  <X className="w-4 h-4 mr-2" />
                  Annuler
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Button onClick={openAddModal} data-testid="add-copro-button" className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
                <Button onClick={() => setEditMode(true)} data-testid="edit-tantiemes-button" variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50">
                  <Calculator className="w-4 h-4 mr-2" />
                  Tantièmes
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Vue desktop : tableau */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="coproprietaires-table">
          <thead>
            <tr className="bg-slate-50/50 border-b border-slate-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Nom
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Email
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Téléphone
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Lot
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Tantièmes
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Rôle
              </th>
              {isAdmin && (
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Mot de passe
                </th>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {coproprietaires.map((copro) => (
              <tr
                key={copro.id}
                className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${
                  copro.id === user.id ? 'bg-indigo-50/30' : ''
                }`}
                data-testid={`copro-row-${copro.id}`}
              >
                <td className="px-4 py-4 font-medium text-slate-900">
                  <div className="flex items-center gap-2">
                    {copro.prenom} {copro.nom}
                    {copro.id === user.id && (
                      <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                        Vous
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-4 text-slate-600">
                  {copro.email || <span className="text-amber-600 italic text-sm">À définir</span>}
                </td>
                <td className="px-4 py-4 text-slate-600">{copro.telephone || '-'}</td>
                <td className="px-4 py-4 text-slate-600">{copro.lot || '-'}</td>
                <td className="px-4 py-4">
                  {editMode ? (
                    <input
                      type="number"
                      step="0.01"
                      value={tantièmesEdit[copro.id] || 0}
                      onChange={(e) => handleTantièmeChange(copro.id, e.target.value)}
                      className="w-20 px-2 py-1 border border-slate-300 rounded focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-center font-semibold"
                    />
                  ) : (
                    <span className="font-semibold text-slate-900 bg-slate-100 px-2 py-1 rounded">
                      {copro.tantiemes}
                    </span>
                  )}
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      copro.role === 'président'
                        ? 'bg-indigo-100 text-indigo-700'
                        : copro.role === 'trésorier'
                        ? 'bg-emerald-100 text-emerald-700'
                        : copro.role === 'secrétaire'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-700'
                    }`}
                  >
                    {copro.role}
                  </span>
                </td>
                {isAdmin && (
                  <td className="px-4 py-4">
                    {copro.provisional_password && copro.must_change_password ? (
                      <div className="flex items-center gap-2">
                        <code className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-mono">
                          {copro.provisional_password}
                        </code>
                        <button
                          onClick={() => handleMarkPasswordTransmitted(copro.id, `${copro.prenom} ${copro.nom}`)}
                          className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          title="Marquer comme transmis"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      </div>
                    ) : copro.must_change_password ? (
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Transmis
                      </span>
                    ) : (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Défini
                      </span>
                    )}
                  </td>
                )}
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    {canEdit(copro) && (
                      <button
                        onClick={() => openEditModal(copro)}
                        data-testid={`edit-copro-button-${copro.id}`}
                        className="text-indigo-600 hover:text-indigo-700 transition-colors p-1"
                        title="Modifier"
                      >
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                    {isPresident && copro.id !== user.id && (
                      <button
                        onClick={() => setDeleteConfirm({ id: copro.id, name: `${copro.prenom} ${copro.nom}` })}
                        data-testid={`delete-copro-button-${copro.id}`}
                        className="text-red-500 hover:text-red-600 transition-colors p-1"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Vue mobile : cartes */}
      <div className="md:hidden space-y-3" data-testid="coproprietaires-mobile">
        {/* Barre d'édition mobile */}
        {editMode && (
          <div className="bg-white border border-emerald-200 rounded-xl p-4 shadow-sm sticky top-0 z-10">
            <div className={`flex items-center justify-between mb-3 ${tantièmesValides ? 'text-emerald-700' : 'text-red-600'}`}>
              <span className="font-medium flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Total : {totalTantiemes.toFixed(2)} / 1000
              </span>
              {tantièmesValides ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-500" />
              )}
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={saveTantiemes} 
                disabled={!tantièmesValides} 
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white text-sm"
              >
                <Save className="w-4 h-4 mr-1" />
                Enregistrer
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { setEditMode(false); fetchCoproprietaires(); }}
                className="flex-1 text-sm"
              >
                <X className="w-4 h-4 mr-1" />
                Annuler
              </Button>
            </div>
          </div>
        )}
        
        {coproprietaires.map((copro) => (
          <div
            key={copro.id}
            className={`bg-white border border-slate-200 rounded-xl shadow-sm p-4 ${
              copro.id === user.id ? 'border-indigo-300 bg-indigo-50/30' : ''
            }`}
            data-testid={`copro-card-${copro.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-slate-900">{copro.prenom} {copro.nom}</h3>
                  {copro.id === user.id && (
                    <span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                      Vous
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-0.5">
                  {copro.email || <span className="text-amber-600 italic">Email à définir</span>}
                </p>
              </div>
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  copro.role === 'président'
                    ? 'bg-indigo-100 text-indigo-700'
                    : copro.role === 'trésorier'
                    ? 'bg-emerald-100 text-emerald-700'
                    : copro.role === 'secrétaire'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-slate-100 text-slate-700'
                }`}
              >
                {copro.role}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 text-sm mb-3">
              <div>
                <p className="text-slate-400 text-xs">Lot</p>
                <p className="font-medium text-slate-700">{copro.lot || '-'}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs">Tantièmes</p>
                {editMode ? (
                  <input
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={tantièmesEdit[copro.id] || 0}
                    onChange={(e) => handleTantièmeChange(copro.id, e.target.value)}
                    className="w-full px-2 py-1 border border-emerald-300 rounded focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 text-center font-semibold text-sm"
                  />
                ) : (
                  <p className="font-semibold text-slate-900">{copro.tantiemes}</p>
                )}
              </div>
              <div>
                <p className="text-slate-400 text-xs">Téléphone</p>
                <p className="font-medium text-slate-700">{copro.telephone || '-'}</p>
              </div>
            </div>

            {/* Section mot de passe pour admin */}
            {isAdmin && (
              <div className="pt-3 border-t border-slate-100">
                {copro.provisional_password && copro.must_change_password ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-slate-400 mb-1">Mot de passe provisoire</p>
                      <code className="bg-amber-100 text-amber-800 px-2 py-1 rounded text-xs font-mono">
                        {copro.provisional_password}
                      </code>
                    </div>
                    <button
                      onClick={() => handleMarkPasswordTransmitted(copro.id, `${copro.prenom} ${copro.nom}`)}
                      className="p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                      title="Marquer comme transmis"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                  </div>
                ) : copro.must_change_password ? (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Mot de passe transmis
                  </span>
                ) : (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Mot de passe défini
                  </span>
                )}
              </div>
            )}

            {/* Bouton modifier */}
            {canEdit(copro) && (
              <div className="pt-3 mt-3 border-t border-slate-100">
                <button
                  onClick={() => openEditModal(copro)}
                  data-testid={`edit-copro-button-mobile-${copro.id}`}
                  className="w-full flex items-center justify-center gap-2 py-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                >
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Modifier mes informations</span>
                </button>
              </div>
            )}
            
            {/* Bouton supprimer (président uniquement, pas soi-même) */}
            {isPresident && copro.id !== user.id && (
              <div className={`pt-3 ${!canEdit(copro) ? 'mt-3 border-t border-slate-100' : ''}`}>
                <button
                  onClick={() => setDeleteConfirm({ id: copro.id, name: `${copro.prenom} ${copro.nom}` })}
                  data-testid={`delete-copro-button-mobile-${copro.id}`}
                  className="w-full flex items-center justify-center gap-2 py-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Retirer de la copropriété</span>
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add Modal */}
      <Dialog open={addModal} onOpenChange={setAddModal}>
        <DialogContent data-testid="add-copro-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Ajouter un copropriétaire
            </DialogTitle>
            <DialogDescription>
              Ajoutez un nouveau membre. Vous pouvez l'ajouter à 0 tantième puis rééquilibrer ensuite via "Tantièmes".
            </DialogDescription>
          </DialogHeader>
          
          {addResult?.success ? (
            <div className="space-y-4 mt-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="font-medium text-emerald-800 mb-2">
                  ✓ {addResult.userName} a été ajouté avec succès !
                </p>
                <div className="bg-white rounded-lg p-3 mt-3">
                  <p className="text-sm text-slate-600 mb-1">Mot de passe provisoire :</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-amber-100 text-amber-800 px-3 py-2 rounded font-mono text-sm">
                      {addResult.provisionalPassword}
                    </code>
                    <button
                      onClick={() => copyToClipboard(addResult.provisionalPassword)}
                      className="p-2 bg-slate-100 rounded hover:bg-slate-200 transition-colors"
                      title="Copier"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {addResult.email && (
                  <p className="text-sm text-slate-600 mt-3">
                    L'utilisateur pourra se connecter avec l'email <strong>{addResult.email}</strong>
                  </p>
                )}
              </div>
              <Button 
                onClick={() => setAddModal(false)} 
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Fermer
              </Button>
            </div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input
                    value={newCopro.prenom}
                    onChange={(e) => setNewCopro({ ...newCopro, prenom: e.target.value })}
                    placeholder="Jean"
                    data-testid="add-prenom-input"
                  />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={newCopro.nom}
                    onChange={(e) => setNewCopro({ ...newCopro, nom: e.target.value })}
                    placeholder="Dupont"
                    data-testid="add-nom-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Email <span className="text-slate-400 text-xs">(optionnel)</span></Label>
                <Input
                  type="email"
                  value={newCopro.email}
                  onChange={(e) => setNewCopro({ ...newCopro, email: e.target.value })}
                  placeholder="email@exemple.com"
                  data-testid="add-email-input"
                />
              </div>
              
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={newCopro.telephone}
                  onChange={(e) => setNewCopro({ ...newCopro, telephone: e.target.value })}
                  placeholder="06 12 34 56 78"
                  data-testid="add-telephone-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Numéro de lot</Label>
                  <Input
                    value={newCopro.lot}
                    onChange={(e) => setNewCopro({ ...newCopro, lot: e.target.value })}
                    placeholder="A12"
                    data-testid="add-lot-input"
                  />
                </div>
                <div>
                  <Label>Tantièmes *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newCopro.tantiemes}
                    onChange={(e) => setNewCopro({ ...newCopro, tantiemes: parseFloat(e.target.value) || 0 })}
                    placeholder="100"
                    data-testid="add-tantiemes-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Rôle</Label>
                <select
                  value={newCopro.role}
                  onChange={(e) => setNewCopro({ ...newCopro, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="add-role-select"
                >
                  <option value="copropriétaire">Copropriétaire</option>
                  <option value="président">Président</option>
                  <option value="trésorier">Trésorier</option>
                  <option value="secrétaire">Secrétaire</option>
                </select>
              </div>
              
              {/* Indicateur tantièmes */}
              <div className={`p-3 rounded-lg border ${newTotalValid ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-center gap-2">
                  {newTotalValid ? (
                    <Check className="w-4 h-4 text-emerald-600" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                  )}
                  <span className={`text-sm font-medium ${newTotalValid ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {(parseFloat(newCopro.tantiemes) || 0) === 0 
                      ? `À 0 tantième — Rééquilibrez ensuite (total actuel: ${totalTantiemes.toFixed(0)}/1000)`
                      : `Total après ajout : ${newTotalWithAdd.toFixed(2)} / 1000`
                    }
                  </span>
                </div>
                {!newTotalValid && (parseFloat(newCopro.tantiemes) || 0) !== 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Ajustez les tantièmes ou mettez 0 pour rééquilibrer après.
                  </p>
                )}
              </div>
              
              <div className="flex gap-3 mt-4">
                <Button
                  onClick={handleAddCoproprietaire}
                  disabled={!newTotalValid || !newCopro.prenom || !newCopro.nom}
                  data-testid="confirm-add-copro-button"
                  className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
                <Button
                  onClick={() => setAddModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Annuler
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent data-testid="edit-copro-modal">
          <DialogHeader>
            <DialogTitle>
              {isPresident && selectedUser?.id !== user.id 
                ? `Modifier ${selectedUser?.prenom} ${selectedUser?.nom}` 
                : 'Modifier mes informations'}
            </DialogTitle>
            <DialogDescription>
              {isPresident 
                ? 'Modifiez les informations du copropriétaire. Les tantièmes se modifient depuis le tableau principal.'
                : 'Vous pouvez modifier vos informations personnelles.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                <Input
                  value={editData.prenom || ''}
                  onChange={(e) => setEditData({ ...editData, prenom: e.target.value })}
                  data-testid="edit-prenom-input"
                />
              </div>
              <div>
                <Label>Nom</Label>
                <Input
                  value={editData.nom || ''}
                  onChange={(e) => setEditData({ ...editData, nom: e.target.value })}
                  data-testid="edit-nom-input"
                />
              </div>
            </div>
            <div>
              <Label>Téléphone</Label>
              <Input
                value={editData.telephone || ''}
                onChange={(e) => setEditData({ ...editData, telephone: e.target.value })}
                data-testid="edit-telephone-input"
              />
            </div>

            {/* Email - modifiable seulement si pas encore défini ou si président/admin */}
            {selectedUser && (!selectedUser.email || isPresident || isMasterAdmin) && (
              <div>
                <Label>Email {!selectedUser.email && <span className="text-amber-600 text-xs">(à définir pour permettre la connexion)</span>}</Label>
                <Input
                  type="email"
                  value={editData.email || ''}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                  placeholder="email@exemple.com"
                  data-testid="edit-email-input"
                />
              </div>
            )}

            {/* Lot - modifiable par le président */}
            {isPresident && (
              <div>
                <Label>Numéro de lot</Label>
                <Input
                  value={editData.lot || ''}
                  onChange={(e) => setEditData({ ...editData, lot: e.target.value })}
                  placeholder="Ex: A12, 3B..."
                  data-testid="edit-lot-input"
                />
              </div>
            )}

            {/* Rôle - modifiable par le président (sauf pour lui-même) */}
            {isPresident && selectedUser?.id !== user.id && (
              <div>
                <Label>Rôle dans la copropriété</Label>
                <select
                  value={editData.role || 'copropriétaire'}
                  onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  data-testid="edit-role-select"
                >
                  <option value="copropriétaire">Copropriétaire</option>
                  <option value="président">Président</option>
                  <option value="trésorier">Trésorier</option>
                  <option value="secrétaire">Secrétaire</option>
                </select>
              </div>
            )}

            {/* Informations non modifiables (pour non-président) */}
            {!isPresident && selectedUser && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 uppercase font-semibold mb-3">Informations non modifiables</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Lot:</span>
                    <span className="ml-2 font-medium">{selectedUser.lot || '-'}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Tantièmes:</span>
                    <span className="ml-2 font-semibold text-slate-900">{selectedUser.tantiemes}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Rôle:</span>
                    <span className="ml-2 font-medium capitalize">{selectedUser.role}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Info tantièmes pour président */}
            {isPresident && selectedUser && (
              <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm text-emerald-700">
                  <Calculator className="w-4 h-4 inline mr-2" />
                  <strong>Tantièmes actuels: {selectedUser.tantiemes}</strong> — Pour modifier les tantièmes, utilisez le bouton "Modifier les tantièmes" dans le tableau.
                </p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleEditSubmit}
                data-testid="save-copro-button"
                className="flex-1 bg-emerald-500 text-white hover:bg-emerald-600"
              >
                Enregistrer
              </Button>
              <Button
                onClick={() => setEditModal(false)}
                variant="outline"
                data-testid="cancel-edit-button"
                className="flex-1"
              >
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir retirer <strong>{deleteConfirm?.name}</strong> de la copropriété ?
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium mb-1">Attention :</p>
                <ul className="list-disc pl-4 space-y-1">
                  <li>Cette action est irréversible</li>
                  <li>Les tantièmes de ce copropriétaire seront retirés</li>
                  <li>Vous devrez rééquilibrer les tantièmes après suppression</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="flex gap-3 mt-6">
            <Button
              onClick={handleDeleteCoproprietaire}
              disabled={isDeleting}
              className="flex-1 bg-red-500 text-white hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </Button>
            <Button
              onClick={() => setDeleteConfirm(null)}
              variant="outline"
              className="flex-1"
              disabled={isDeleting}
            >
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Coproprietaires;
